import type { OrderWithTimeline } from "../page";

interface Props {
  order: OrderWithTimeline;
}

export default function OrderTimeline({ order }: Props) {
  if (order.status === "cancelled") return null;

  return (
    <div className="bg-[#141414] border border-white/5 rounded-2xl p-6">
      <h2 className="text-white font-bold text-sm mb-6 flex items-center gap-2" style={{ fontFamily: "'Cairo', sans-serif" }}>
        <div className="w-7 h-7 flex items-center justify-center bg-amber-400/10 rounded-lg">
          <i className="ri-route-line text-amber-400 text-sm"></i>
        </div>
        تتبع الطلب
      </h2>

      <div className="relative">
        {/* Vertical line */}
        <div className="absolute right-[18px] top-5 bottom-5 w-px bg-white/5"></div>

        <div className="space-y-0">
          {order.timeline.map((step, idx) => {
            const isLast = idx === order.timeline.length - 1;
            return (
              <div key={step.status} className={`relative flex gap-5 ${isLast ? "" : "pb-7"}`}>
                {/* Icon circle */}
                <div className={`relative z-10 w-9 h-9 flex items-center justify-center rounded-full flex-shrink-0 transition-all ${
                  step.active
                    ? "bg-amber-400 text-black ring-4 ring-amber-400/20"
                    : step.completed
                    ? "bg-green-400/15 border border-green-400/40 text-green-400"
                    : "bg-[#0a0a0a] border border-white/8 text-gray-700"
                }`}>
                  {step.completed && !step.active ? (
                    <i className="ri-check-line text-sm"></i>
                  ) : (
                    <i className={`${step.icon} text-sm`}></i>
                  )}
                  {/* Pulse for active */}
                  {step.active && (
                    <span className="absolute inset-0 rounded-full bg-amber-400/30 animate-ping"></span>
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 pt-1">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className={`text-sm font-bold leading-tight ${
                        step.active
                          ? "text-amber-400"
                          : step.completed
                          ? "text-green-400"
                          : "text-gray-600"
                      }`} style={{ fontFamily: "'Cairo', sans-serif" }}>
                        {step.label}
                        {step.active && (
                          <span className="mr-2 text-[10px] bg-amber-400/15 text-amber-400 px-2 py-0.5 rounded-full font-bold">
                            الحالة الحالية
                          </span>
                        )}
                      </h3>
                      <p className={`text-xs mt-1 leading-relaxed ${
                        step.completed ? "text-gray-500" : "text-gray-700"
                      }`} style={{ fontFamily: "'Cairo', sans-serif" }}>
                        {step.description}
                      </p>
                    </div>

                    {step.completed && (
                      <div className="flex-shrink-0 text-left">
                        <p className={`text-[11px] font-bold ${step.active ? "text-amber-400/70" : "text-gray-600"}`}
                          style={{ fontFamily: "'Cairo', sans-serif" }}>
                          {new Date(step.date).toLocaleDateString("ar-SA", {
                            month: "short", day: "numeric",
                          })}
                        </p>
                        <p className="text-[10px] text-gray-700 text-left" style={{ fontFamily: "'Cairo', sans-serif" }}>
                          {new Date(step.date).toLocaleTimeString("ar-SA", {
                            hour: "2-digit", minute: "2-digit",
                          })}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Connector card for active step */}
                  {step.active && (
                    <div className="mt-3 bg-amber-400/5 border border-amber-400/15 rounded-xl px-4 py-3 flex items-center gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse flex-shrink-0"></div>
                      <p className="text-amber-400/80 text-xs" style={{ fontFamily: "'Cairo', sans-serif" }}>
                        سيتم تحديث حالة طلبك تلقائياً عند كل مرحلة
                      </p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
